// The open state of the sidebar groups: kept in memory and saved with the preferences (1d).
import { useSyncExternalStore } from "react";
import { announcePreference } from "../preference-events";

type Groups = Record<string, boolean>;

/** Where 1c kept the state on this browser; read once so the first save carries it over. */
const LEGACY_KEY = "cs-groups";

let groups: Groups | null = null;
const listeners = new Set<() => void>();

function takeLegacy(): Groups {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(LEGACY_KEY) ?? "{}");
    localStorage.removeItem(LEGACY_KEY);
    if (!parsed || typeof parsed !== "object") return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, boolean] => typeof entry[1] === "boolean",
      ),
    );
  } catch {
    return {};
  }
}

export function sidebarGroups(): Groups {
  groups ??= takeLegacy();
  return groups;
}

/** Shows the saved open state. */
export function applySidebarGroups(next: Groups): void {
  groups = next;
  for (const listener of listeners) listener();
}

/** Opens or closes a group (open by default) and announces the new state so it is saved. */
export function toggleSidebarGroup(group: string): void {
  const current = sidebarGroups();
  const next = { ...current, [group]: !(current[group] ?? true) };
  applySidebarGroups(next);
  announcePreference({ sidebarGroups: next });
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** The open state of each group, re-rendering when it changes. */
export const useSidebarGroups = (): Groups => useSyncExternalStore(subscribe, sidebarGroups);
